import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Rocket, DollarSign, ShieldCheck, Lock, Settings, TrendingUp, AlertTriangle, Check, X,
} from "lucide-react";
import { RiskBanner, RiskFooter } from "@/components/RiskDisclaimer";

const Chip = ({ children, testId }) => (
  <span
    data-testid={testId}
    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-300 text-[11px] font-mono uppercase tracking-[0.15em] font-semibold"
  >
    {children}
  </span>
);

const Section = ({ id, icon: Icon, eyebrow, title, children, className = "", accent = "emerald" }) => (
  <section
    id={id}
    data-testid={`pricing-section-${id}`}
    className={`rounded-2xl border border-white/10 bg-[#0F111A]/70 backdrop-blur-sm p-6 sm:p-8 scroll-mt-24 ${className}`}
  >
    <div className="flex items-center gap-3 mb-4">
      <span className={`grid place-items-center w-10 h-10 rounded-full shrink-0 ${accent === "amber" ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
          {eyebrow}
        </div>
        <h2 className="font-display text-lg sm:text-2xl font-bold text-slate-100 leading-tight">
          {title}
        </h2>
      </div>
    </div>
    <div className="text-[14px] sm:text-[15px] text-slate-300 leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-[#0B0C12] text-slate-100 flex flex-col relative overflow-hidden">
      {/* Subtle grid backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06] bg-[linear-gradient(#10B981_1px,transparent_1px),linear-gradient(90deg,#10B981_1px,transparent_1px)] bg-[size:56px_56px]"
      />
      <div className="relative z-10 flex flex-col min-h-screen">
        <RiskBanner />

        <header className="max-w-5xl w-full mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="pricing-back-home" className="flex items-center gap-3 group">
            <img
              src="/brand/tradingox-logo.png"
              alt="TradingoX"
              className="w-10 h-10 rounded-full ring-1 ring-white/10 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
            />
            <div className="font-display text-lg font-bold tracking-tight">TradingoX</div>
          </Link>
          <Link
            to="/"
            data-testid="pricing-back-btn"
            className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100 transition"
          >
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
        </header>

        <main className="max-w-3xl w-full mx-auto px-4 sm:px-6 pt-4 sm:pt-6 pb-16 flex-1">
          {/* Hero */}
          <div className="text-center mb-10">
            <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-emerald-300 mb-3">
              pricing
            </div>
            <h1 data-testid="pricing-hero-title" className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.02]">
              <span className="text-emerald-400">TRADINGO</span>-X
            </h1>
            <p className="mt-3 font-display text-lg sm:text-2xl font-bold text-slate-100 tracking-tight">
              Trade Smarter. On Autopilot.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Chip testId="pricing-chip-free">Free to join</Chip>
              <Chip testId="pricing-chip-fee">1.25% only on profits</Chip>
              <Chip testId="pricing-chip-broker">Your money stays with your broker</Chip>
            </div>
          </div>

          {/* Big pillars */}
          <div className="grid sm:grid-cols-3 gap-3 mb-10">
            {[
              { icon: X, title: "No membership fee.", tone: "emerald" },
              { icon: X, title: "No monthly subscription.", tone: "emerald" },
              { icon: X, title: "No TradingoX fee on losing trades.", tone: "emerald" },
            ].map((p, i) => (
              <div
                key={p.title}
                data-testid={`pricing-pillar-${i}`}
                className="rounded-2xl border border-emerald-400/20 bg-emerald-500/[0.06] p-5 text-center"
              >
                <p.icon className="w-6 h-6 text-emerald-300 mx-auto mb-2" />
                <div className="font-display font-bold text-base sm:text-lg text-slate-100 leading-tight">
                  {p.title}
                </div>
              </div>
            ))}
          </div>

          <p className="text-slate-300 text-[15px] leading-relaxed mb-10 text-center max-w-2xl mx-auto">
            TradingoX makes automated stock trading simple. Our system works automatically — scouting for trading opportunities and executing trades through your connected brokerage account.
          </p>

          {/* Sections */}
          <div className="space-y-6">
            <Section
              id="you-profit-we-earn"
              icon={DollarSign}
              eyebrow="pricing model"
              title="You profit. We earn."
            >
              <p>
                TradingoX charges just <b className="text-emerald-300">1.25% of the realized profit</b> on each individual profitable trade.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Check className="w-4 h-4 text-emerald-300" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold">Profit</span>
                  </div>
                  <div className="font-display text-lg font-bold text-white">→ 1.25% fee</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <X className="w-4 h-4 text-slate-300" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">No profit</span>
                  </div>
                  <div className="font-display text-lg font-bold text-white">→ No profit-based fee</div>
                </div>
              </div>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-300 font-semibold">
                Simple · Transparent · Performance-based
              </p>
            </Section>

            <Section
              id="your-broker"
              icon={ShieldCheck}
              eyebrow="custody & control"
              title="Your money stays with your broker."
            >
              <p>
                TradingoX does <b>not</b> hold, custody, or process withdrawals of your investment funds.
              </p>
              <p>
                Your money and securities remain in your brokerage account with <b className="text-emerald-300">Alpaca</b>. TradingoX provides the automated trading technology; your broker handles custody and brokerage transactions.
              </p>
              <div className="mt-4 flex flex-wrap justify-center sm:justify-start gap-2 text-center">
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-slate-100 text-sm font-semibold">
                  <Lock className="w-4 h-4 text-emerald-300" /> Your account
                </span>
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-slate-100 text-sm font-semibold">
                  <DollarSign className="w-4 h-4 text-emerald-300" /> Your money
                </span>
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-slate-100 text-sm font-semibold">
                  <Settings className="w-4 h-4 text-emerald-300" /> Your control
                </span>
              </div>
            </Section>

            <Section
              id="set-it-and-trade"
              icon={Rocket}
              eyebrow="how simple it is"
              title="Set it up. Let it trade."
            >
              <p>
                Experience can be helpful — but TradingoX is designed to make automated trading accessible and simple.
              </p>
              <div className="mt-4 rounded-xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/10 to-transparent p-5 text-center">
                <div className="font-display text-base sm:text-xl font-bold text-white tracking-tight">
                  Free to join · Automatic to trade · Pay when you profit
                </div>
              </div>
            </Section>
          </div>

          {/* Founder sign-off */}
          <div className="mt-10 rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/12 via-transparent to-transparent p-6 sm:p-8 text-center">
            <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
              <span className="text-emerald-400">TRADINGO</span>-X
            </div>
            <p className="mt-2 font-mono text-[11px] sm:text-[12px] uppercase tracking-[0.3em] text-emerald-300 font-bold">
              Trade Smarter · On Autopilot
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                data-testid="pricing-cta-back"
                className="rounded-full h-11 px-6 inline-flex items-center bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm"
              >
                <TrendingUp className="w-4 h-4 mr-2" /> Launch Terminal
              </Link>
              <Link
                to="/about"
                data-testid="pricing-cta-about"
                className="rounded-full h-11 px-6 inline-flex items-center border border-white/15 hover:border-white/30 text-slate-200 hover:text-white text-sm"
              >
                Read the story →
              </Link>
            </div>
          </div>

          {/* Risk disclosure */}
          <section
            data-testid="pricing-risk"
            className="mt-10 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 sm:p-6"
          >
            <div className="flex items-start gap-3">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-amber-500/15 text-amber-300 shrink-0 mt-0.5">
                <AlertTriangle className="w-4 h-4" />
              </span>
              <div>
                <div className="font-display text-base sm:text-lg font-bold text-amber-100">
                  Beware — Risk Disclosure
                </div>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                  Trading and investing involve substantial risk, including possible loss of principal. Profits are not guaranteed. TradingoX does not guarantee returns or protection against losses. Brokerage, regulatory, exchange, or other third-party fees may apply.
                </p>
                <Link
                  to="/terms"
                  className="inline-block mt-3 text-xs font-mono uppercase tracking-[0.2em] text-amber-200 hover:text-white underline underline-offset-4"
                >
                  Read full terms & disclaimer →
                </Link>
              </div>
            </div>
          </section>
        </main>

        <RiskFooter />
      </div>
    </div>
  );
}
